import { useLanguage } from "@/i18n/LanguageContext";
import LongPage from "@/components/LongPage";
import dashboardImg from "@/assets/products/monitoring-oee/dashboard.jpg";
import paretoImg from "@/assets/products/monitoring-oee/pareto.jpg";
import oeeTimelineImg from "@/assets/products/monitoring-oee/oee-timeline.jpg";
import shiftConfirmImg from "@/assets/products/monitoring-oee/shift-confirm.jpg";

export default function MonitoringOEE() {
  const { t } = useLanguage();
  return (
    <LongPage
      breadcrumb={[{ name: "Products", to: "/#modules" }]}
      eyebrow={t("Monitoring & OEE")}
      title={t("See every machine. Find lost hours. Make more product.")}
      seoTitle="Real-Time OEE Monitoring — BEEDIGIT"
      seoDescription="Live OEE, downtime and output for every work center — even legacy machines. Recover hidden capacity in weeks, not quarters. Book a demo."
      description={t("BEEDIGIT Monitoring turns your shop floor into a live dashboard. Know what every machine is doing right now, why it stopped, and how much output you just lost — so you can fix it during the shift, not next month.")}
      bullets={[
        "Live OEE on every machine — within 24 hours of install",
        "Automatic downtime capture with reasons confirmed by operators",
        "Recover 5–15% capacity from machines you already own",
        "Works on any asset — even 20-year-old machines without a PLC",
      ]}
      schema="product"
      primaryCta={{ label: "Book a 30-min demo", to: "/get-started" }}
      secondaryCta={{ label: "Talk to expert", to: "/get-started" }}
      ctaHeading="Stop guessing. Start seeing."
      ctaBody="Show us one work center. In 30 minutes we'll show you the OEE you've been missing, the hours you've been losing, and the capacity you already own."
      sections={[
        {
          kind: "personas",
          eyebrow: "Who it's for",
          heading: "If output is your job, this is your tool",
          intro:
            "From the boardroom to the shop floor, BEEDIGIT replaces opinions with facts — so every conversation about production starts from the same number.",
          items: [
            { role: "CEO / Owner", pain: "Is the factory really maxed out — or am I about to spend on a machine I don't need?", value: "Proof of hidden capacity before the next CAPEX request lands on your desk." },
            { role: "COO / Ops Director", pain: "Every plant tells me a different story about why we missed plan.", value: "One live number per work center — the end of conflicting Monday reports." },
            { role: "Plant Manager", pain: "I find out about today's problems tomorrow.", value: "See the bottleneck while the shift is still running — and act on it." },
            { role: "Production Manager", pain: "Targets slip and nobody can say exactly when or why.", value: "Hour-by-hour output vs target, on a screen the whole team can see." },
            { role: "Lean / CI Lead", pain: "We chase the loudest problem, not the biggest one.", value: "A Pareto of real losses — so your improvement backlog finally bites." },
            { role: "CFO", pain: "Downtime costs us money I can't quantify.", value: "Lost hours, lost output, lost margin — in numbers your board will respect." },
          ],
        },
        {
          kind: "prose",
          eyebrow: "The problem",
          heading: "Your factory is leaking capacity. You just can't see it.",
          body:
            "Most plants run at 45–60% OEE and don't know it. Micro-stops vanish. Slow cycles get smoothed over. Downtime is explained from memory, hours after it happened — if at all. Spreadsheets land on Monday with numbers nobody trusts, and the next CAPEX request goes out anyway.\n\nMeanwhile, the capacity is already on your floor. You paid for it. You just can't see it.\n\nBEEDIGIT puts every machine on one screen — running, idle, stopped, slow, behind plan — in real time. The arguments stop. The improvement starts.",
        },
        {
          kind: "steps",
          eyebrow: "How it works",
          heading: "Live OEE in three steps — and one day",
          intro:
            "No rip-and-replace. No PLC project. We connect three simple signals and your work center starts producing real OEE the same day we install.",
          items: [
            { n: 1, title: "We connect", body: "Our engineers clip non-invasive sensors to your machines — old or new, PLC or no PLC. No downtime, no machine modification, no IT migration." },
            { n: 2, title: "You see", body: "Within hours, every connected work center is live on your dashboard with real OEE, real downtime, real output — anchored to the job order it's running." },
            { n: 3, title: "You improve", body: "Supervisors classify losses on the floor. Reports land automatically. Your team stops debating what happened and starts fixing it." },
          ],
        },
        {
          kind: "features",
          eyebrow: "What you get",
          heading: "Everything to run production, day one",
          cols: 3,
          items: [
            { title: "The wall dashboard", desc: "Every machine, every line — running, idle or down — on one screen. Designed to be readable from across the shop floor." },
            { title: "Live OEE per machine", desc: "Availability, Performance, Quality — calculated live from real signals, not end-of-shift guesses." },
            { title: "Automatic downtime", desc: "The moment a machine stops, the clock starts. Operators add the reason from a tablet — no paper, no memory." },
            { title: "Loss Pareto", desc: "The top 5 reasons you're losing money this week, ranked and clickable. Your improvement backlog writes itself." },
            { title: "Plan vs actual, hourly", desc: "Are you on pace for today's target? You'll know by 10am, not 6pm." },
            { title: "Smart alerts", desc: "Downtime over 15 minutes? Speed dropping? Target slipping? The right person gets a notification — automatically." },
            { title: "Reports that send themselves", desc: "Shift reports, daily summaries, weekly board packs — generated, formatted and emailed without anyone touching Excel." },
            { title: "Works on any machine", desc: "20-year-old extruder? CNC from last week? Legacy press without a PLC? We connect it the same day." },
            { title: "Your data, your ERP", desc: "Push live output to SAP, Oracle, Dynamics, Odoo, Power BI — out of the box." },
          ],
        },
        {
          kind: "callout",
          eyebrow: "First OEE in 24 hours",
          heading: "Stop waiting for an 18-month digital transformation.",
          body: "You don't need a master data project. You don't need a PLC upgrade. You don't need to retrain your operators for six months. We bring the hardware, we clip it on, and you have a real OEE number on your screen by the end of the day. Grow from there at your own pace.",
          href: "/hardware",
          linkLabel: "See the hardware",
        },
        {
          kind: "gains",
          eyebrow: "What it's worth",
          heading: "What you'll get back — in your language",
          intro:
            "Every role in your business gets a clear win. Here's what your team will actually see on Monday morning.",
          items: [
            { role: "CEO", cares: "Profit, growth, capital discipline.", gain: "5–15% more output from machines you already own — before approving a new line.", kpis: "OEE trend, capacity utilization, cost per unit.", pitch: "Earn more from what you already paid for." },
            { role: "COO", cares: "Throughput across plants.", gain: "One scorecard, every site, every shift — the end of conflicting reports.", kpis: "OEE by plant, plan adherence, unclassified downtime.", pitch: "Run every plant by the same number." },
            { role: "Plant Manager", cares: "Hitting plan today.", gain: "Spot the broken machine in 60 seconds, not at end of shift.", kpis: "Live OEE, current downtime, shift-close readiness.", pitch: "Fix it while the shift is still running." },
            { role: "Production Manager", cares: "Output and targets.", gain: "Hourly progress vs target — so you adjust at 10am, not apologize at 6pm.", kpis: "Hourly output, speed variance, confirmed output.", pitch: "Catch a bad shift while there's still time to save it." },
            { role: "Lean / CI", cares: "Real improvement, not theatre.", gain: "A weekly Pareto of your top 5 losses — ranked, costed, clickable.", kpis: "Top losses, repeat events, action impact.", pitch: "Stop guessing where to focus. Know." },
            { role: "CFO", cares: "ROI and the next CAPEX.", gain: "Quantified lost production in EUR — before signing the cheque on new equipment.", kpis: "Lost hours, downtime cost, utilization.", pitch: "Prove the capacity exists before you spend." },
            { role: "Maintenance", cares: "Breakdowns and uptime.", gain: "Every breakdown logged automatically, ranked by impact on production.", kpis: "Breakdown duration, repeat failures, MTTR.", pitch: "Maintain what's actually hurting you." },
            { role: "Energy", cares: "kWh per unit.", gain: "Find machines burning power while producing nothing.", kpis: "kWh by machine, energy during downtime.", pitch: "Stop paying to run idle machines." },
            { role: "CIO", cares: "Secure, scalable shop-floor data.", gain: "A clean, single source of truth — built on hardware and APIs your team controls.", kpis: "Connected machines, data completeness, uptime.", pitch: "Own your shop-floor data layer." },
          ],
        },
        {
          kind: "useCases",
          eyebrow: "In the real world",
          heading: "What changes on Monday morning",
          items: [
            { persona: "The plant manager", scenario: "Walks in, opens one screen, sees Line 3 is down and Line 5 is slow — before her first coffee.", outcome: "Intervenes in 5 minutes. Saves the shift." },
            { persona: "The supervisor", scenario: "A machine stops. A notification hits the tablet. The operator taps the reason in 10 seconds.", outcome: "Every loss explained — at the source, not from memory." },
            { persona: "The CI lead", scenario: "Pulls up last month's Pareto. The #1 loss is changeover on Line 2 — 87 hours, EUR 41k.", outcome: "Stops guessing. Runs a SMED project where it actually pays back." },
            { persona: "The CFO", scenario: "Reviews a CAPEX request for a new line. Pulls up the OEE of the existing line: 52%.", outcome: "Postpones the purchase. Funds an improvement project instead." },
            { persona: "The legacy machine", scenario: "A 22-year-old extruder, no PLC, no network. Engineer clips on a current sensor before lunch.", outcome: "First real OEE number on that machine by the end of the shift." },
            { persona: "The Monday meeting", scenario: "Reports arrive automatically in everyone's inbox at 7am. Same numbers, no spreadsheets.", outcome: "Meeting starts with decisions, not data wrangling." },
          ],
        },
        {
          kind: "mockups",
          eyebrow: "See it",
          heading: "Designed for the shop floor, not the boardroom",
          intro: "Big numbers. Clear colors. Readable from across the room — because that's where decisions actually get made.",
          items: [
            { src: dashboardImg, alt: "Live work-center dashboard", caption: "Every machine, one screen. Green is good. Red is money walking out the door." },
            { src: oeeTimelineImg, alt: "OEE timeline", caption: "Your OEE story, hour by hour. Spot the bad shift before it becomes a bad week." },
            { src: paretoImg, alt: "Downtime Pareto", caption: "Your top 5 losses, ranked. This is your improvement backlog — written for you." },
            { src: shiftConfirmImg, alt: "Shift close workflow", caption: "No shift closes with missing reasons. Clean data, every day, automatically." },
          ],
        },
        {
          kind: "kpi",
          eyebrow: "The numbers that matter",
          heading: "Four KPIs on every wall, every shift",
          intro: "Live. Drillable to the event. Exportable to your ERP. No more arguing about what \"OEE\" means.",
          items: [
            { value: "OEE", label: "Live, every machine, every shift" },
            { value: "A · P · Q", label: "What's hurting you most" },
            { value: "Pareto", label: "Your top 5 losses, ranked" },
            { value: "Plan ↔ Actual", label: "Are you on pace, right now" },
          ],
        },
        {
          kind: "split",
          eyebrow: "Why BEEDIGIT",
          heading: "What you have today vs what you'll have next month",
          leftTitle: "Today",
          rightTitle: "With BEEDIGIT",
          left: [
            "Monday reports built from memory and Excel",
            "Downtime explained — sometimes — at end of shift",
            "Three systems, three numbers, three arguments",
            "Legacy machines stay invisible",
            "Improvement priorities decided by who shouts loudest",
            "CAPEX requests based on \"we feel maxed out\"",
          ],
          right: [
            "Live OEE, every machine, every shift",
            "Every stop captured automatically with a reason",
            "One screen, one number, one truth",
            "Every machine connected — including the old ones",
            "Improvements driven by a costed loss Pareto",
            "CAPEX based on proof, not gut feel",
          ],
        },
        {
          kind: "chips",
          eyebrow: "Hardware included",
          heading: "We bring the kit. You keep producing.",
          intro: "Non-invasive. No machine downtime to install. Streams encrypted, with a 24-hour local buffer in case the network blinks.",
          items: [
            "BEEDIGIT HUB",
            "HoneyPlug current sensor",
            "BEECT current transformer",
            "BEEPAD operator tablet",
            "GSM · Wi-Fi · Ethernet",
            "Discrete I/O · pulse · 4-20 mA",
          ],
        },
        {
          kind: "chips",
          eyebrow: "Plays well with your stack",
          heading: "Your ERP, your BI, your way",
          intro: "Push live shop-floor data into the tools your business already runs on — no integration project required.",
          items: [
            "factory.online", "SAP", "Oracle EBS / Fusion", "Microsoft Dynamics", "Odoo",
            "Power BI", "Tableau", "Data lakes (CSV / JSON / REST)",
            "MQTT", "OPC UA", "REST / Webhooks",
          ],
        },
        {
          kind: "split",
          eyebrow: "Your rollout",
          heading: "From zero to live in 90 days",
          leftTitle: "Weeks 1–4",
          rightTitle: "Weeks 5–12",
          left: [
            "We walk your floor — pick the first 3 work centers",
            "Sensors installed, signals validated, dashboards live",
            "Operators trained in under an hour, on tablets they already understand",
            "Your first real OEE baseline — and your first \"we had no idea\" moment",
          ],
          right: [
            "Roll out to every remaining machine",
            "Loss reasons tuned with your supervisors",
            "ERP plan-vs-actual wired in — Monday meetings get easier",
            "Auto-reports replace the weekend spreadsheet — for good",
          ],
        },
        {
          kind: "callout",
          eyebrow: "Officially certified",
          heading: "The only MES certified by INCIT for raising your SIRI index",
          body: "Industry 4.0 grant programs, government incentives, sustainability reports — BEEDIGIT counts. We're the first platform officially recognized for moving the needle on the Smart Industry Readiness Index.",
          href: "/siri",
          linkLabel: "See how BEEDIGIT raises SIRI",
        },
        {
          kind: "faq",
          eyebrow: "FAQ",
          heading: "What buyers ask us first",
          items: [
            {
              q: "Our machines are 20+ years old and have no PLC. Will this work?",
              a: "Yes — that's exactly what we're built for. We use non-invasive sensors (current clamps, pulse counters, simple I/O) and our own HUB. No machine modification, no downtime to install. Most legacy work centers are producing live OEE within hours.",
            },
            {
              q: "How fast can we see real results?",
              a: "First real OEE number on the first machine: same day. First Pareto of losses you can actually act on: end of week 2. First measurable improvement in throughput: typically within 60–90 days.",
            },
            {
              q: "What kind of ROI should we expect?",
              a: "Our customers typically recover 5–15% of capacity from existing machines in the first year — usually enough to pay for the whole platform several times over, and often enough to delay or cancel a planned CAPEX. We'll build a tailored business case during the demo.",
            },
            {
              q: "Will our operators actually use it?",
              a: "Yes — because we designed it for them, not for IT. The tablet interface is closer to a phone app than a SCADA screen. Training is under an hour. Adoption on our customer sites is consistently above 90% within the first month.",
            },
            {
              q: "How is this different from a generic OEE dashboard?",
              a: "Dashboards show you a number. BEEDIGIT helps you change it. Same data model powers MES, Energy, Quality and CMMS — so when you're ready to expand into execution, traceability, energy or maintenance, you don't start over. Most customers do.",
            },
            {
              q: "Will it connect to our ERP and BI?",
              a: "Out of the box — SAP, Oracle, Microsoft Dynamics, Odoo, Power BI, Tableau. Bi-directional REST/JSON API, plus MQTT, OPC UA and good old CSV when you need it.",
            },
            {
              q: "Is our data safe? What if the internet drops?",
              a: "All traffic is encrypted (MQTT over TLS, mutual auth). Devices buffer 24 hours locally — if your network blinks, you lose nothing. Role-based access for operators, supervisors and admins. Audit trail on everything.",
            },
            {
              q: "What does it actually cost?",
              a: "We price per connected work center, with hardware included. Most customers see payback inside a single quarter. Book the demo — we'll show you the numbers tied to your machines and your shifts.",
            },
          ],
        },
        {
          kind: "related",
          eyebrow: "Next steps",
          heading: "Customers usually add these next",
          items: [
            { title: "Manufacturing Execution", href: "/products/manufacturing-execution", desc: "Digital job orders, paperless output confirmation, QR-based traceability — connected to your ERP." },
            { title: "Energy Management", href: "/products/energy-management", desc: "kWh per product, real EnPIs, ISO 50001-aligned — stop paying for energy you don't sell." },
            { title: "CMMS", href: "/products/cmms", desc: "Tie maintenance to the actual production losses it causes — fix what costs you most." },
          ],
        },
      ]}
    />
  );
}